import React, {useMemo, useState} from 'react';
import {Box, ButtonBase} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import BuildIcon from '@mui/icons-material/Build';
import CloseIcon from '@mui/icons-material/Close';
import SanitizerIcon from '@mui/icons-material/Sanitizer';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {CARTE, NAVY, TEXTE, TIMELINE, TITRE_SECTION, VERDICT_COULEURS} from '../../tokens.js';
import PiluleVerdict from '../PiluleVerdict.js';
import PiluleActe from '../PiluleActe.js';
import {filtrerLignes, formatDate, iconeLigne, libelleVerdictLigne} from './fiche.selectors.js';

const PAS = 20;
const FILTRES = [
    {id: 'tout', libelle: 'Tout'},
    {id: 'lavages', libelle: 'Lavages'},
    {id: 'maintenance', libelle: 'Maintenance'},
    {id: 'graffitis', libelle: 'Graffitis'},
    {id: 'collecte', libelle: 'Collecte', collecte: true}, // lot 8 : seulement quand le bouton Collecte est actif
];

const thSx = {fontWeight: 700, py: '10px', px: '12px', borderBottom: `1px solid ${CARTE.separateur}`, textAlign: 'left'};
const tdSx = {py: '12px', px: '12px', borderBottom: `1px solid ${CARTE.separateur}`, verticalAlign: 'middle'};

const pilulePointillee = {display: 'inline-flex', alignItems: 'center', gap: '8px', py: '4px', pl: '6px', pr: '12px', borderRadius: '999px', border: '1px dashed #B5BCC6', bgcolor: '#F6F8F4', color: TITRE_SECTION, fontSize: 12, fontWeight: 700};

function PastilleHorloge() {
    return (
        <Box component="span" sx={{width: 20, height: 20, borderRadius: '50%', border: '1.5px dashed #B5BCC6', display: 'inline-flex', alignItems: 'center', justifyContent: 'center'}}>
            <ScheduleIcon sx={{fontSize: 12}}/>
        </Box>
    );
}

function CelluleVerdict({ligne}) {
    if (ligne.nature === 'planifie') return <Box component="span" sx={pilulePointillee}><PastilleHorloge/>Planifié</Box>;
    const c = VERDICT_COULEURS[ligne.verdict] || VERDICT_COULEURS.ni;
    const libelle = ligne.nature === 'collecte' ? (ligne.verdict === 'imp' ? 'Collecte impossible' : 'Collectée') : libelleVerdictLigne(ligne.verdict, ligne);
    return (
        <Box component="span" sx={{display: 'inline-flex', py: '4px', pl: '6px', pr: '12px', borderRadius: '999px', bgcolor: c.fond}}>
            <PiluleVerdict verdict={ligne.verdict} libelle={libelle}/>
        </Box>
    );
}

function CelluleActes({ligne}) {
    return (
        <Box component="span" sx={{display: 'inline-flex', gap: '6px', flexWrap: 'wrap'}}>
            {ligne.actes.map((a, i) => (
                a.type === 'planifie'
                    ? <Box key={i} component="span" sx={{display: 'inline-flex', px: '10px', py: '3px', borderRadius: '999px', bgcolor: '#F6F8F4', color: TITRE_SECTION, fontSize: 12, fontWeight: 700}}>{a.libelle}</Box>
                    : <PiluleActe key={i} type={a.type} libelle={a.libelle}/>
            ))}
        </Box>
    );
}

const ICONES = {lavage: WaterDropIcon, maintenance: BuildIcon, impossible: CloseIcon, graffiti: SanitizerIcon, planifie: ScheduleIcon, collecte: LocalShippingIcon};

/** Cellule timeline : trait vertical continu (sur toute la hauteur de la cellule) + pastille 32 px. */
function CelluleTimeline({ligne}) {
    const {type, couleur} = iconeLigne(ligne);
    const Icone = ICONES[type];
    const pointille = type === 'planifie';
    return (
        <Box component="td" aria-hidden="true" data-timeline={type} sx={{width: 56, minWidth: 56, p: 0, position: 'relative', borderBottom: `1px solid ${CARTE.separateur}`, verticalAlign: 'middle', '&::before': {content: '""', position: 'absolute', top: 0, bottom: 0, left: '27px', width: '2px', bgcolor: TIMELINE.trait}}}>
            <Box component="span" sx={{position: 'absolute', top: '50%', left: '12px', transform: 'translateY(-50%)', width: 32, height: 32, boxSizing: 'border-box', borderRadius: '50%', bgcolor: couleur, border: '2px solid #FFFFFF', outline: pointille ? '1.5px dashed #8A929C' : 'none', outlineOffset: '-4px', boxShadow: '0 1px 3px rgba(16,46,105,.25)', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Icone sx={{fontSize: 16}}/>
            </Box>
        </Box>
    );
}

/** Historique du point (R5), pleine largeur. `lignes` : sortie de `lignesHistorique`. */
export default function FicheHistorique({lignes, collecte = false, onPdf}) {
    const [filtre, setFiltre] = useState('tout');
    const [visibles, setVisibles] = useState(PAS);
    const filtrees = useMemo(() => filtrerLignes(lignes, filtre), [lignes, filtre]);
    const nbPassages = useMemo(() => lignes.filter((l) => l.nature === 'passage').length, [lignes]);
    const affichees = filtrees.slice(0, visibles);

    return (
        <Box component="section" aria-labelledby="fiche-historique" sx={{bgcolor: CARTE.fond, border: `1px solid ${CARTE.bordure}`, borderRadius: `${CARTE.radius}px`, boxShadow: CARTE.ombre, overflow: 'hidden'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: '16px', px: '24px', py: '16px', borderBottom: `1px solid ${CARTE.separateur}`, flexWrap: 'wrap'}}>
                <Box component="h2" id="fiche-historique" sx={{m: 0, fontSize: 12, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', color: NAVY}}>Historique du point</Box>
                <Box component="span" sx={{fontSize: 12, color: TITRE_SECTION}}>{`${nbPassages} passage${nbPassages > 1 ? 's' : ''} · du plus récent au plus ancien`}</Box>
                <Box sx={{flex: '1 1 40px'}}/>
                <Box role="group" aria-label="Filtrer par acte" sx={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
                    {FILTRES.filter((f) => collecte || !f.collecte).map((f) => {
                        const actif = f.id === filtre;
                        return (
                            <ButtonBase key={f.id} aria-pressed={actif} onClick={() => { setFiltre(f.id); setVisibles(PAS); }}
                                        sx={{height: 32, px: '14px', borderRadius: '999px', fontSize: 12, fontWeight: actif ? 700 : 600, border: `1px solid ${actif ? NAVY : CARTE.bordure}`, bgcolor: actif ? NAVY : '#FFFFFF', color: actif ? '#FFFFFF' : TEXTE}}>
                                {f.libelle}
                            </ButtonBase>
                        );
                    })}
                </Box>
            </Box>
            <Box sx={{overflowX: 'auto'}}>
                <Box component="table" sx={{width: '100%', minWidth: 780, fontSize: 13, borderCollapse: 'collapse'}}>
                    <Box component="thead">
                        <Box component="tr" sx={{color: TITRE_SECTION, fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase'}}>
                            <Box component="th" scope="col" aria-label="Type" sx={{...thSx, p: 0, width: 56, minWidth: 56}}/>
                            <Box component="th" scope="col" sx={{...thSx, width: 150}}>Date</Box>
                            <Box component="th" scope="col" sx={{...thSx, width: 260}}>Verdict</Box>
                            <Box component="th" scope="col" sx={thSx}>Actes</Box>
                            <Box component="th" scope="col" sx={{...thSx, pr: '24px', width: 120, textAlign: 'right'}}>Détail</Box>
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {affichees.length === 0 && (
                            <Box component="tr"><Box component="td" colSpan={5} sx={{...tdSx, px: '24px', color: TITRE_SECTION}}>—</Box></Box>
                        )}
                        {affichees.map((l) => {
                            const grise = l.nature === 'planifie';
                            const sousLigne = l.nature === 'planifie' ? 'à venir' : (l.heure || '—');
                            return (
                                <Box component="tr" key={l.cle} sx={grise ? {color: TITRE_SECTION} : {}}>
                                    <CelluleTimeline ligne={l}/>
                                    <Box component="td" sx={{...tdSx, whiteSpace: 'nowrap'}}>
                                        <Box component="strong" sx={{fontWeight: 700}}>{formatDate(l.date)}</Box><br/>
                                        <Box component="span" sx={{fontSize: 12, color: TITRE_SECTION}}>{sousLigne}</Box>
                                    </Box>
                                    <Box component="td" sx={tdSx}><CelluleVerdict ligne={l}/></Box>
                                    <Box component="td" sx={tdSx}><CelluleActes ligne={l}/></Box>
                                    <Box component="td" sx={{...tdSx, pr: '24px', textAlign: 'right'}}>
                                        {l.nature === 'passage' && l.interventionId != null
                                            ? <ButtonBase onClick={() => onPdf(l.interventionId)} sx={{fontWeight: 700, fontSize: 13, color: NAVY, whiteSpace: 'nowrap'}}>Fiche PDF ↗</ButtonBase>
                                            : '—'}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
            {filtrees.length > visibles && (
                <Box sx={{display: 'flex', justifyContent: 'center', px: '24px', py: '14px'}}>
                    <ButtonBase onClick={() => setVisibles((v) => v + PAS)} sx={{height: 38, px: '20px', gap: '8px', borderRadius: '999px', border: `1px solid ${NAVY}`, bgcolor: '#FFFFFF', color: NAVY, fontSize: 13, fontWeight: 700}}>
                        Voir plus
                        <ExpandMoreIcon sx={{fontSize: 16}}/>
                    </ButtonBase>
                </Box>
            )}
        </Box>
    );
}
